import React, { useEffect, useMemo } from 'react'
import './ImageViewerListHeaderStyle.css'
import { ButtonGroup, Form } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import SearchWithFilter, {
  FilterSelection,
} from '@components/common/SearchWithFilter/SearchWithFilter.component'
import { IFile } from '@dataTypes/folder.types'

// Add props
interface ImageViewerListHeaderProps {
  view: 'grid' | 'list'
  onToggleView: (view: 'grid' | 'list') => void
  searchTerm: string
  onSearchChange: (searchTerm: string) => void
  favoritesOnly: boolean
  onFavoritesOnlyChange: (favoritesOnly: boolean) => void
  selectedCount: number
  totalCount: number
  onSelectAll: () => void
  onDeselectAll: () => void
  searchFilters: FilterSelection
  onFilterChange: (filters: FilterSelection) => void
  onResetFilters: () => void
  files: IFile[]
}

function ImageViewerListHeader({
  view,
  onToggleView,
  searchTerm,
  onSearchChange,
  favoritesOnly,
  onFavoritesOnlyChange,
  selectedCount,
  totalCount,
  onSelectAll,
  onDeselectAll,
  searchFilters,
  onFilterChange,
  onResetFilters,
  files,
}: ImageViewerListHeaderProps): React.ReactElement {
  const isIndeterminate = selectedCount > 0 && selectedCount < totalCount
  const isChecked = totalCount > 0 && selectedCount === totalCount

  useEffect(() => {
    const checkbox = document.getElementById(
      'select-all-checkbox',
    ) as HTMLInputElement
    if (checkbox) {
      checkbox.indeterminate = isIndeterminate
    }
  }, [isIndeterminate])

  // Generate filter data from files
  const filterData = useMemo(() => {
    const uniqueTypes = Array.from(
      new Set(
        files
          .map((f) => f.file_type)
          .filter((type): type is string => Boolean(type)),
      ),
    )
    return {
      type: uniqueTypes,
    }
  }, [files])

  const filterLabels = {
    type: 'Type',
  }

  return (
    <div className="image-viewer-list-header">
      <div className="left-div">
        <h1>Results</h1>
        <div className="selection-actions">
          <div className="select-all-section">
            <Form.Check
              type="checkbox"
              id="select-all-checkbox"
              label="Select all"
              checked={isChecked}
              onChange={(e) => {
                if (e.target.checked || isIndeterminate) {
                  onSelectAll()
                } else {
                  onDeselectAll()
                }
              }}
              disabled={totalCount === 0}
              className="select-all-checkbox"
            />
            <span className="selection-count">
              {selectedCount} item(s) selected
            </span>
          </div>
          <Button
            className="button button-secondary"
            onClick={onDeselectAll}
            disabled={selectedCount === 0}
            size="sm"
          >
            Clear All
          </Button>
        </div>
      </div>
      <div className="right-div">
        <Form.Check
          type="checkbox"
          label="Show Starred"
          className=""
          checked={favoritesOnly}
          onChange={(e) => onFavoritesOnlyChange(e.target.checked)}
        />
        <SearchWithFilter
          searchIcon="ti ti-search"
          filterIcon="mgc_settings_2_line"
          filterLabel=""
          placeholder="Search files"
          searchQuery={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          onFilterChange={onFilterChange}
          onReset={onResetFilters}
          filters={filterData}
          filterLabels={filterLabels}
          className="header-search-filter"
          showResetButton={true}
        />
        <ButtonGroup className="">
          <Button
            className={`btn${view === 'list' ? '-active' : ''}`}
            onClick={() => onToggleView('list')}
          >
            <i className="ti ti-list" />
            {' List'}
          </Button>
          <Button
            className={`btn${view === 'grid' ? '-active' : ''}`}
            onClick={() => onToggleView('grid')}
          >
            <i className="ti ti-layout-grid-filled" />
            Grid
          </Button>
        </ButtonGroup>
      </div>
    </div>
  )
}

export default ImageViewerListHeader
